import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useCopyToClipboard } from 'usehooks-ts';

import { CHECK_ICON_SVG, CLIPBOARD_ICON_SVG } from '@/utils/icon-svgs';

type UseClipboardOptions = {
  timeout?: number;
  successMessage?: string;
  errorMessage?: string;
};

type UseClipboardReturn = {
  copy: (text: string) => Promise<void>;
  isCopied: boolean;
  icon: string;
};

export function useClipboard({
  timeout = 2000,
  successMessage = 'Copied to clipboard',
  errorMessage = 'Failed to copy',
}: UseClipboardOptions = {}): UseClipboardReturn {
  const [, copyToClipboard] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);

  // Reset isCopied state after timeout
  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false);
      }, timeout);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isCopied, timeout]);

  const copy = useCallback(
    async (text: string) => {
      try {
        const success = await copyToClipboard(text);
        if (success) {
          setIsCopied(true);
          toast.success(successMessage);
        } else {
          toast.error(errorMessage);
        }
      } catch (error) {
        console.error('Failed to copy:', error);
        toast.error(errorMessage);
      }
    },
    [copyToClipboard, successMessage, errorMessage],
  );

  const icon = isCopied ? CHECK_ICON_SVG : CLIPBOARD_ICON_SVG;

  return { copy, isCopied, icon };
}
